<template>
    <TDesktop
        v-if="isAppLoaded && canStart"
        class="p-desktop desktop"
        :class="{'desktop__focus-supports': isKeyboardNavigation}"
        :style="uiColors"
    >
        <template #overlay>
            <div>
                <AForbidden v-if="isEmbedForbidden"/>
                <div
                    class="desktop__overlay"
                    v-if="isOverlay"
                    @click="isOverlay = false"
                />
            </div>
        </template>
        <template #header>
            <ODesktopMessage
                v-if="messageTitle"
                :title="messageTitle"
                :description="messageDescription"
            />
            <ODStoroHeader />
        </template>
        <template #sidepanel>
            <router-view />
        </template>
        <template #footer>
            <ODFooter v-if="isFooter"/>
        </template>
        <template #modal>
            <OBaseModal
                v-if="modalOrganism"
                :organism="modalOrganism.component"
                :data="modalOrganism.data"
                @onClose="closeModal"
            />
        </template>
    </TDesktop>
    <AInitialLoader v-else />
</template>

<script>
import '../../../assets/images/generated-svg';

import {
    mapGetters,
    mapActions,
    mapState
} from 'vuex';

import * as Sentry from "@sentry/vue";

import canStart from '../../../utils/mixins/can-start';
import TDesktop from '../../templates/t-desktop/t-desktop';
import ODFooter from '../../organisms/o-d-footer/o-d-footer';
import OBaseModal from '../../organisms/o-modal-base/o-modal-base';
import AInitialLoader from '../../atoms/a-initial-loader/a-initial-loader';
import keyboardNavigation from '../../../utils/mixins/keyboard-navigation';
import ODStoroHeader from '../../organisms/o-d-storo-header/o-d-storo-header';
import ODesktopMessage from '../../organisms/o-desktop-message/o-desktop-message';

import {
    APP_TYPES
} from "@/utils/constants/app-constants";

import {
    TYPES,
    WEB_GUIDE,
    DESKTOP_START,
    DESKTOP_SEARCH
} from '../../../utils/constants/pages';

import {
    SHOW_MODAL,
    SHOW_OVERLAY
} from '../../../utils/constants/bus-events-constants';
import AForbidden from "@/components/atoms/a-forbidden/a-forbidden";

export default {
    name: 'PDesktop',
    components: {
        AForbidden,
        ODFooter,
        TDesktop,
        OBaseModal,
        ODStoroHeader,
        AInitialLoader,
        ODesktopMessage
    },
    mixins: [keyboardNavigation, canStart],
    data() {
        return {
            isOverlay: false,
            modalOrganism: null
        }
    },
    computed: {
        ...mapState({
            messageTitle: state => state.map.meta.otg_emergency_banner_header,
            messageDescription: state => state.map.meta.otg_emergency_banner_text,
            isEmbedForbidden: state => state.isEmbedForbidden,
        }),
        ...mapGetters([
            'isAppLoaded',
            'getMapConfigProp',
            'getDenormalisedObjectById'
        ]),
        isFooter() {
            return this.$route.name.includes(DESKTOP_START) || this.$route.name.includes(DESKTOP_SEARCH);
        },
        uiColors() {
            return this.getMapConfigProp('ui_colors') || null;
        }
    },
    watch: {
        isAppLoaded () {
            if(this.isAppLoaded) this.changeLocaleAtStart()
        }
    },
    created() {
        Sentry.setTag("app-type", APP_TYPES.DESKTOP);
        const themes = this.$route.query.theme ? [this.$route.query.theme ] : ['desktop', 'default'];
        this.$store.commit('setActiveMapTheme', themes);
        this.initEventsListeners();
        this.loadMapData();
    },
    beforeDestroy() {
        this.deactivateEventsListeners();
    },
    methods: {
        ...mapActions([
            'fetchMapData',
            'changeLocaleAtStart'
        ]),
        async loadMapData() {
            try {
                const mapId = Number(this.$route.params.mapId);
                await this.fetchMapData(mapId);
                if (this.$route.params.qrId) this.proceedQrCodeData();
            } catch(error) {
                console.log('Data fetch error', error);
            }
        },
        initEventsListeners() {
            this.$bus.$on(SHOW_MODAL, this.showModal);
            this.$bus.$on(SHOW_OVERLAY, this.showOverlay);
        },
        deactivateEventsListeners() {
            this.$bus.$off(SHOW_MODAL, this.showModal);
            this.$bus.$off(SHOW_OVERLAY, this.showOverlay);
        },
        showModal(data) {
            this.modalOrganism = data;
        },
        closeModal() {
            this.modalOrganism = null;
        },
        showOverlay(val) {
            this.isOverlay = val;
        },
        proceedQrCodeData() {
            try {
                const qrId = Number(this.$route.params.qrId);
                const qrObj = this.getDenormalisedObjectById(qrId);
                const start = qrObj.meta?.qr.start || '';
                const destination = qrObj.meta?.qr.destination || '';
                const noClosestEntrance = qrObj.meta?.qr.noClosestEntrance || '';
                const params = {
                    name: WEB_GUIDE,
                    params: {
                        type: TYPES.CARE,
                        qr: true
                    },
                    query: {
                        'redirect-from': 'qr'
                    }
                };

                if (!start && !destination) return;

                if (start && destination) {
                    params.query.from = start;
                    params.query.to = destination;
                } else if (start) {
                    params.query.from = start;
                } else if (destination && noClosestEntrance) {
                    params.query.to = destination;
                } else if (destination) {
                    params.query.to = destination;
                    params.params.closest = true;
                }

                this.$router.push(params);
            } catch(error) {
                console.warn('QR code is not valid');
            }
        }
    }
}
</script>

<style lang="scss">
@import "p-desktop";
@import "p-desktop-print";
</style>
